import { REWARD_CODE_DEFINITIONS } from "@/features/payment/paymentDefinitions";
import { formatInteger, getMessages } from "@/i18n/runtime";
import { waitForPaint } from "@/shared/ui/waitForPaint";
import type {
  HoverIdlePaymentState,
  RewardCodeId,
} from "@/features/payment/types";

export type DataPanelLoadResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

export type DataPanelEraseProgressResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

export type DataPanelBackupResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: "cancelled" | string };

export interface DataPanelOptions {
  readonly container: HTMLElement;
  readonly loadPaymentState: () => HoverIdlePaymentState;
  readonly onBackup: () => Promise<DataPanelBackupResult>;
  readonly onLoadFile: (file: File) => Promise<DataPanelLoadResult>;
  readonly onRedeemRewardCode: (code: string) => {
    readonly ok: boolean;
    readonly reason?: string;
    readonly payment: HoverIdlePaymentState;
  };
  readonly onEraseProgress: () => Promise<DataPanelEraseProgressResult>;
}

export interface DataPanel {
  update(paymentState: HoverIdlePaymentState): void;
  dispose(): void;
}

const ERASE_PROGRESS_CONFIRM_TIMEOUT_MS = 5000;

// Save import/export, reward codes, and progress reset controls for settings.data.
export function createDataPanel(options: DataPanelOptions): DataPanel {
  const messages = getMessages();
  const cleanups: Array<() => void> = [];

  const dataMessages = messages.settings.data;

  const root = document.createElement("div");
  root.className = "hover-idle-data-panel";

  const tokenSummary = document.createElement("p");
  tokenSummary.className = "hover-idle-data-token";

  const backupZone = document.createElement("div");
  backupZone.className =
    "hover-idle-data-section hover-idle-data-section-backup";

  const backupHeading = document.createElement("h3");
  backupHeading.className = "hover-idle-data-section-heading";
  backupHeading.textContent = dataMessages.backupHeading;

  const rewardCodeZone = document.createElement("div");
  rewardCodeZone.className = "hover-idle-data-section";

  const rewardCodeHeading = document.createElement("h3");
  rewardCodeHeading.className = "hover-idle-data-section-heading";
  rewardCodeHeading.textContent = dataMessages.rewardCodeHeading;

  const rewardCodeDescription = document.createElement("p");
  rewardCodeDescription.className = "hover-idle-data-description";
  rewardCodeDescription.textContent = dataMessages.rewardCodeDescription;

  const rewardCodeForm = document.createElement("div");
  rewardCodeForm.className = "hover-idle-data-actions";

  const rewardCodeInput = document.createElement("input");
  rewardCodeInput.type = "text";
  rewardCodeInput.className = "hover-idle-data-input";
  rewardCodeInput.dataset.field = "reward-code";
  rewardCodeInput.placeholder = dataMessages.rewardCodePlaceholder;
  rewardCodeInput.autocomplete = "off";
  rewardCodeInput.spellcheck = false;
  rewardCodeInput.setAttribute("aria-label", dataMessages.rewardCodeAria);

  const rewardCodeButton = document.createElement("button");
  rewardCodeButton.type = "button";
  rewardCodeButton.className = "hover-idle-data-button";
  rewardCodeButton.dataset.action = "redeem-reward-code";
  rewardCodeButton.textContent = messages.common.receive;

  const rewardCodeStatus = document.createElement("p");
  rewardCodeStatus.className = "hover-idle-data-status";
  rewardCodeStatus.setAttribute("role", "status");
  rewardCodeStatus.setAttribute("aria-live", "polite");

  const setRewardCodeFormStatus = (
    message: string,
    kind: "info" | "error" | "warn" | "" = "",
  ): void => {
    rewardCodeStatus.textContent = message;
    rewardCodeStatus.dataset.kind = kind;
  };

  const rewardCodeList = document.createElement("div");
  rewardCodeList.className = "hover-idle-data-code-list";
  const redeemedRewardCodeIds: RewardCodeId[] = [];

  const backupDescription = document.createElement("p");
  backupDescription.className = "hover-idle-data-description";
  backupDescription.textContent = dataMessages.backupDescription;

  const actions = document.createElement("div");
  actions.className = "hover-idle-data-actions";

  const backupButton = document.createElement("button");
  backupButton.type = "button";
  backupButton.className = "hover-idle-data-button";
  backupButton.textContent = dataMessages.backupButton;

  const loadButton = document.createElement("button");
  loadButton.type = "button";
  loadButton.className = "hover-idle-data-button";
  loadButton.textContent = dataMessages.loadButton;

  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = ".txt,text/plain";
  fileInput.hidden = true;

  const dangerZone = document.createElement("div");
  dangerZone.className = "hover-idle-data-danger-zone";

  const dangerHeading = document.createElement("h3");
  dangerHeading.className = "hover-idle-data-danger-heading";
  dangerHeading.textContent = dataMessages.dangerHeading;

  const dangerDescription = document.createElement("p");
  dangerDescription.className = "hover-idle-data-description";
  dangerDescription.textContent = dataMessages.dangerDescription;

  const eraseProgressButton = document.createElement("button");
  eraseProgressButton.type = "button";
  eraseProgressButton.className =
    "hover-idle-data-button hover-idle-data-button-danger";
  eraseProgressButton.dataset.action = "erase-progress";
  eraseProgressButton.textContent = dataMessages.eraseButton;

  const status = document.createElement("p");
  status.className = "hover-idle-data-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");

  const setStatus = (
    message: string,
    kind: "info" | "error" | "warn" | "" = "",
  ): void => {
    status.textContent = message;
    status.dataset.kind = kind;
  };

  const syncPayment = (paymentState: HoverIdlePaymentState): void => {
    tokenSummary.textContent = dataMessages.tokenSummary(
      formatInteger(paymentState.token),
    );
    for (const definition of REWARD_CODE_DEFINITIONS) {
      const id = definition.id;
      const redeemed = paymentState.redeemedRewardCodes.includes(id);
      if (redeemed && !redeemedRewardCodeIds.includes(id)) {
        const item = document.createElement("div");
        item.className = "hover-idle-data-code-item";

        const code = document.createElement("span");
        code.className = "hover-idle-data-code-label";
        code.textContent = dataMessages.rewardCodeEntry(
          id,
          formatInteger(definition.rewardToken),
        );

        const state = document.createElement("span");
        state.className = "hover-idle-data-code-state";
        state.textContent = dataMessages.redeemed;
        state.dataset.redeemed = "true";

        item.append(code, state);
        rewardCodeList.append(item);
        redeemedRewardCodeIds.push(id);
      }
    }
  };

  const setNonEraseButtonsDisabled = (disabled: boolean): void => {
    backupButton.disabled = disabled;
    loadButton.disabled = disabled;
    rewardCodeButton.disabled = disabled;
    rewardCodeInput.disabled = disabled;
  };

  const setButtonsDisabled = (disabled: boolean): void => {
    setNonEraseButtonsDisabled(disabled);
    eraseProgressButton.disabled = disabled;
  };

  let eraseProgressArmed = false;
  let eraseProgressTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  const clearEraseProgressTimeout = (): void => {
    if (eraseProgressTimeoutHandle !== null) {
      clearTimeout(eraseProgressTimeoutHandle);
      eraseProgressTimeoutHandle = null;
    }
  };

  const disarmEraseProgress = (): void => {
    clearEraseProgressTimeout();
    eraseProgressArmed = false;
    eraseProgressButton.textContent = dataMessages.eraseButton;
    eraseProgressButton.dataset.armed = "false";
    dangerZone.dataset.pending = "false";
    setNonEraseButtonsDisabled(false);
  };

  const armEraseProgress = (): void => {
    eraseProgressArmed = true;
    eraseProgressButton.textContent = dataMessages.eraseButtonConfirm;
    eraseProgressButton.dataset.armed = "true";

    dangerZone.dataset.pending = "false";

    setNonEraseButtonsDisabled(false);

    setStatus(
      dataMessages.eraseWarning(ERASE_PROGRESS_CONFIRM_TIMEOUT_MS / 1000),
      "warn",
    );

    clearEraseProgressTimeout();
    eraseProgressTimeoutHandle = setTimeout(() => {
      disarmEraseProgress();
      setStatus(dataMessages.eraseCancelled, "info");
    }, ERASE_PROGRESS_CONFIRM_TIMEOUT_MS);
  };

  const handleBackup = async (): Promise<void> => {
    disarmEraseProgress();
    setButtonsDisabled(true);
    try {
      const result = await options.onBackup();
      if (result.ok) {
        setStatus(dataMessages.backupSucceeded, "info");
      } else if (result.reason === "cancelled") {
        setStatus(dataMessages.backupCancelled, "info");
      } else {
        setStatus(dataMessages.backupFailed(result.reason), "error");
      }
    } catch (error) {
      setStatus(dataMessages.backupFailed(reasonFromUnknown(error)), "error");
    } finally {
      setButtonsDisabled(false);
    }
  };

  const handleBackupClick = (): void => {
    void handleBackup();
  };

  const handleLoadClick = (): void => {
    disarmEraseProgress();
    fileInput.value = "";
    fileInput.click();
  };

  const handleRewardCodeRedeem = (): void => {
    disarmEraseProgress();
    const code = rewardCodeInput.value.trim();
    if (code.length === 0) {
      setRewardCodeFormStatus(dataMessages.rewardCodeEmpty, "error");
      return;
    }

    const result = options.onRedeemRewardCode(code);
    syncPayment(result.payment);
    if (result.ok) {
      rewardCodeInput.value = "";
      setRewardCodeFormStatus(dataMessages.rewardCodeApplied, "info");
      return;
    }
    if (result.reason === "already-redeemed") {
      setRewardCodeFormStatus(dataMessages.rewardCodeAlreadyRedeemed, "error");
      return;
    }
    setRewardCodeFormStatus(dataMessages.rewardCodeInvalid, "error");
  };

  const handleFileChange = async (): Promise<void> => {
    const file = fileInput.files?.[0];
    if (!file) return;
    setStatus(messages.common.loading, "info");
    setButtonsDisabled(true);
    try {
      const result = await options.onLoadFile(file);
      if (!result.ok) {
        setStatus(dataMessages.loadFailed(result.reason), "error");
      }
    } catch (error) {
      setStatus(dataMessages.loadFailed(reasonFromUnknown(error)), "error");
    } finally {
      setButtonsDisabled(false);
    }
  };

  const handleFileChangeEvent = (): void => {
    void handleFileChange();
  };

  let eraseProgressInFlight = false;

  const handleEraseProgress = async (): Promise<void> => {
    if (eraseProgressInFlight) {
      return;
    }
    if (!eraseProgressArmed) {
      armEraseProgress();
      return;
    }
    clearEraseProgressTimeout();
    eraseProgressArmed = false;
    eraseProgressButton.dataset.armed = "false";
    eraseProgressInFlight = true;
    eraseProgressButton.dataset.pending = "true";
    dangerZone.dataset.pending = "true";
    eraseProgressButton.textContent = dataMessages.eraseInProgress;
    setStatus(dataMessages.eraseInProgress, "info");
    setButtonsDisabled(true);
    await waitForPaint();
    let shouldKeepPending = false;
    try {
      const result = await options.onEraseProgress();
      if (result.ok) {
        shouldKeepPending = true;
        return;
      } else {
        setStatus(dataMessages.eraseFailed(result.reason), "error");
      }
    } catch (error) {
      setStatus(dataMessages.eraseFailed(reasonFromUnknown(error)), "error");
    } finally {
      if (!shouldKeepPending) {
        eraseProgressInFlight = false;
        eraseProgressButton.dataset.pending = "false";
        dangerZone.dataset.pending = "false";
        setButtonsDisabled(false);
      }
    }
  };

  const handleEraseProgressClick = (): void => {
    void handleEraseProgress();
  };

  backupButton.addEventListener("click", handleBackupClick);
  loadButton.addEventListener("click", handleLoadClick);
  rewardCodeInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      rewardCodeButton.click();
    }
  });
  rewardCodeButton.addEventListener("click", handleRewardCodeRedeem);
  fileInput.addEventListener("change", handleFileChangeEvent);
  eraseProgressButton.addEventListener("click", handleEraseProgressClick);

  cleanups.push(() =>
    backupButton.removeEventListener("click", handleBackupClick),
  );
  cleanups.push(() => loadButton.removeEventListener("click", handleLoadClick));
  cleanups.push(() =>
    rewardCodeButton.removeEventListener("click", handleRewardCodeRedeem),
  );
  cleanups.push(() =>
    fileInput.removeEventListener("change", handleFileChangeEvent),
  );
  cleanups.push(() =>
    eraseProgressButton.removeEventListener("click", handleEraseProgressClick),
  );

  rewardCodeForm.append(rewardCodeInput, rewardCodeButton);
  rewardCodeZone.append(
    rewardCodeHeading,
    tokenSummary,
    rewardCodeDescription,
    rewardCodeForm,
    rewardCodeStatus,
    rewardCodeList,
  );
  actions.append(backupButton, loadButton, fileInput);
  backupZone.append(backupHeading, backupDescription, actions);
  dangerZone.append(dangerHeading, dangerDescription, eraseProgressButton);
  root.append(backupZone, rewardCodeZone, dangerZone, status);
  options.container.replaceChildren(root);
  syncPayment(options.loadPaymentState());

  return {
    update(paymentState: HoverIdlePaymentState): void {
      syncPayment(paymentState);
    },
    dispose(): void {
      clearEraseProgressTimeout();
      for (const cleanup of cleanups) cleanup();
      root.remove();
    },
  };
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
